/**
 * Knowledge Gaps Page - Super Admin Only
 * Shows detected missing documents in the knowledge base
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listKnowledgeGaps,
  updateKnowledgeGap,
  deleteKnowledgeGap,
  KnowledgeGap,
} from '@/services/api/knowledge-gaps';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertTriangle,
  CheckCircle,
  EyeOff,
  ExternalLink,
  MoreHorizontal,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'destructive' },
  added: { label: 'Added', variant: 'default' },
  ignored: { label: 'Ignored', variant: 'secondary' },
};

export function KnowledgeGapsPage() {
  const [statusFilter, setStatusFilter] = useState('all');
  const queryClient = useQueryClient();

  const { data: gaps = [], isLoading } = useQuery({
    queryKey: ['knowledge-gaps', statusFilter],
    queryFn: () => listKnowledgeGaps(statusFilter),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateKnowledgeGap(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps'] });
      toast.success('Gap status updated');
    },
    onError: () => toast.error('Failed to update gap'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteKnowledgeGap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps'] });
      toast.success('Gap deleted');
    },
    onError: () => toast.error('Failed to delete gap'),
  });

  const pendingCount = gaps.filter((g) => g.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Knowledge Gaps</h1>
        <p className="text-muted-foreground">
          Documents detected as missing from the knowledge base
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Detected Gaps
              {pendingCount > 0 && (
                <Badge variant="destructive">{pendingCount} pending</Badge>
              )}
            </CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="added">Added</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : gaps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No knowledge gaps detected yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Source URL</TableHead>
                  <TableHead className="text-center">Requests</TableHead>
                  <TableHead>First Seen</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gaps.map((gap) => (
                  <GapRow
                    key={gap.id}
                    gap={gap}
                    onUpdate={(status) =>
                      updateMutation.mutate({ id: gap.id, status })
                    }
                    onDelete={() => deleteMutation.mutate(gap.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GapRow({
  gap,
  onUpdate,
  onDelete,
}: {
  gap: KnowledgeGap;
  onUpdate: (status: string) => void;
  onDelete: () => void;
}) {
  const badge = STATUS_BADGES[gap.status] || STATUS_BADGES.pending;

  return (
    <TableRow>
      <TableCell className="font-medium">{gap.reference}</TableCell>
      <TableCell>{gap.document_type}</TableCell>
      <TableCell>{gap.official_source}</TableCell>
      <TableCell>
        {gap.suggested_url ? (
          <a
            href={gap.suggested_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
          >
            Link <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="outline">{gap.request_count}</Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {new Date(gap.detected_at).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {new Date(gap.last_seen_at).toLocaleDateString()}
      </TableCell>
      <TableCell>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {gap.status !== 'added' && (
              <DropdownMenuItem onClick={() => onUpdate('added')}>
                <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                Mark as Added
              </DropdownMenuItem>
            )}
            {gap.status !== 'ignored' && (
              <DropdownMenuItem onClick={() => onUpdate('ignored')}>
                <EyeOff className="mr-2 h-4 w-4 text-gray-500" />
                Mark as Ignored
              </DropdownMenuItem>
            )}
            {gap.status !== 'pending' && (
              <DropdownMenuItem onClick={() => onUpdate('pending')}>
                <AlertTriangle className="mr-2 h-4 w-4 text-amber-500" />
                Reset to Pending
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
